import { MessageType, NO_HANDLE, type EmoteKind } from "odyssey-shared";

/**
 * The emote frames the page reads and sends (P2.19-T8; ADR-114 decisions 6.1 and 6.2, PROTOCOL.md 6). The text schema
 * has already checked every field of a server message when it reaches here (1 to 16 set names of the sets' pattern),
 * so nothing is checked twice. An `emojiShown` or an `actionShown` is read here and handed on as the character's
 * handle, the emote's kind and its id, which the page turns into an icon key for the emoji over characters
 * (`world-ui/emoji/`).
 */

/**
 * What passes between the game's connection and the emote wheel: the way to send, the account's sets as the gateway
 * sent them, and the connection's end.
 */
export interface EmoteFeed {
  /**
   * Gives the wheel the way to send on whatever connection the scene holds.
   *
   * @param send The sender.
   */
  bindSend(send: (kind: EmoteKind, id: number, target: number) => boolean): void;
  /**
   * Hands the wheel the gateway's `emotes`.
   *
   * @param sets The set names the account holds.
   */
  receive(sets: readonly string[]): void;
  /**
   * The connection ended.
   */
  ended(): void;
}

/**
 * The sets of an `emotes` frame, or null for any other message.
 *
 * @param message A parsed server message.
 * @returns The set names, or null.
 * @example
 * readEmotes({ type: MessageType.Emotes, sets: ["base"] });
 * // ["base"]
 */
export function readEmotes(message: Readonly<Record<string, unknown>> & { readonly type: number }): readonly string[] | null {
  return message.type === MessageType.Emotes ? (message["sets"] as readonly string[]) : null;
}

/**
 * A picture the server showed over a character in view: the character's handle, and the emote whose picture it is by
 * its kind and its id within the kind, an emoji's from `emojiShown` or an action's from `actionShown` (ADR-114's
 * amendment of 2026-10-09).
 */
export interface ShownEmoji {
  readonly handle: number;
  readonly kind: EmoteKind;
  readonly id: number;
}

/**
 * The emoji of an `emojiShown` frame, or null for any other message.
 *
 * @param message A parsed server message.
 * @returns The character's handle, the kind `emoji` and the emoji's id, or null.
 * @example
 * readEmojiShown({ type: MessageType.EmojiShown, handle: 7, id: 1 });
 * // { handle: 7, kind: "emoji", id: 1 }
 */
export function readEmojiShown(message: Readonly<Record<string, unknown>> & { readonly type: number }): ShownEmoji | null {
  return message.type === MessageType.EmojiShown ? { handle: message["handle"] as number, kind: "emoji", id: message["id"] as number } : null;
}

/**
 * The picture of an `actionShown` frame, which follows an action's line, or null for any other message.
 *
 * @param message A parsed server message.
 * @returns The character's handle, the kind `action` and the action's id, or null.
 * @example
 * readActionShown({ type: MessageType.ActionShown, handle: 7, id: 5 });
 * // { handle: 7, kind: "action", id: 5 }
 */
export function readActionShown(message: Readonly<Record<string, unknown>> & { readonly type: number }): ShownEmoji | null {
  return message.type === MessageType.ActionShown ? { handle: message["handle"] as number, kind: "action", id: message["id"] as number } : null;
}

/**
 * The `emote` message for an entry of the wheel.
 *
 * @param kind The emote's kind.
 * @param id Its id within its kind.
 * @param target The entity handle an action names, or `NO_HANDLE`; an emoji always carries `NO_HANDLE`, as its
 *   schema requires.
 * @returns The message, which `encodeText` holds to the schema.
 * @example
 * emoteMessage("action", 22, 7);
 * // { type: MessageType.Emote, kind: "action", id: 22, target: 7 }
 */
export function emoteMessage(kind: EmoteKind, id: number, target: number): { readonly type: MessageType } & Readonly<Record<string, unknown>> {
  return { type: MessageType.Emote, kind, id, target: kind === "emoji" ? NO_HANDLE : target };
}
